import React, { useState, useEffect } from 'react';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import BreadcrumbTrail from '../../components/navigation/BreadcrumbTrail';
import GlossaryHeader from './components/GlossaryHeader';
import FilterBar from './components/FilterBar';
import GlossaryEntryCard from './components/GlossaryEntryCard';
import BulkOperationsPanel from './components/BulkOperationsPanel';
import AddWordModal from './components/AddWordModal';
import EmptyState from './components/EmptyState';
import UsageDetailsModal from './components/UsageDetailsModal';
import { Checkbox } from '../../components/ui/Checkbox';
import { fetchGlossaryTermsWithUsage, fetchPlays, updateGlossaryTerm, deleteGlossaryTerm, addGlossaryTerm } from '../../services/glossaryService';
import { useAuth } from '../../contexts/AuthContext';
import AIGlossaryGenerator from './components/AIGlossaryGenerator';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { generateAudioForTerm, retryFailedAudioGeneration } from '../../services/glossaryAudioGenerationService';



const GlossaryManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [playFilter, setPlayFilter] = useState('all');
  const [alphabetFilter, setAlphabetFilter] = useState('all');
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [generatedTerms, setGeneratedTerms] = useState([]);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [glossaryEntries, setGlossaryEntries] = useState([]);
  const [plays, setPlays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usageModalEntry, setUsageModalEntry] = useState(null);
  const [audioRetrying, setAudioRetrying] = useState(false);
  const [audioRetryStatus, setAudioRetryStatus] = useState(null);
  const { userProfile, isAuthenticated } = useAuth();

  const mockUser = {
    name: 'Admin User',
    role: 'admin',
    email: 'admin@bardplay.com'
  };

  const mockNotifications = [
    {
      id: 1,
      type: 'info',
      title: 'New words identified',
      message: '15 archaic words from Romeo and Juliet need definitions',
      timestamp: new Date(Date.now() - 3600000),
      read: false
    }
  ];

  // Load glossary data from database
  useEffect(() => {
    loadGlossaryData();
  }, []);

  const loadGlossaryData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [termsData, playsData] = await Promise.all([
        fetchGlossaryTermsWithUsage(),
        fetchPlays()
      ]);
      setGlossaryEntries(termsData);
      setPlays(playsData);
    } catch (err) {
      console.error('Error loading glossary data:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const filterEntries = () => {
    return glossaryEntries?.filter(entry => {
      const matchesSearch = 
        entry?.word?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        entry?.definition?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        entry?.playTitle?.toLowerCase()?.includes(searchTerm?.toLowerCase());

      const matchesStatus = 
        statusFilter === 'all' ||
        (statusFilter === 'complete' && entry?.definition && entry?.audioUrl) ||
        (statusFilter === 'incomplete' && !entry?.definition) ||
        (statusFilter === 'no-audio' && entry?.definition && !entry?.audioUrl);

      const matchesPlay = 
        playFilter === 'all' ||
        entry?.playId === playFilter;

      const matchesAlphabet = 
        alphabetFilter === 'all' ||
        (alphabetFilter === 'a-e' && /^[a-e]/i?.test(entry?.word)) ||
        (alphabetFilter === 'f-j' && /^[f-j]/i?.test(entry?.word)) ||
        (alphabetFilter === 'k-o' && /^[k-o]/i?.test(entry?.word)) ||
        (alphabetFilter === 'p-t' && /^[p-t]/i?.test(entry?.word)) ||
        (alphabetFilter === 'u-z' && /^[u-z]/i?.test(entry?.word));

      return matchesSearch && matchesStatus && matchesPlay && matchesAlphabet;
    });
  };

  const handleEdit = async (id, updates) => {
    try {
      await updateGlossaryTerm(id, updates);
      // Reload data to get updated usage statistics
      await loadGlossaryData();
    } catch (err) {
      console.error('Error updating glossary term:', err);
      alert('Failed to update glossary term: ' + err?.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this word?')) {
      try {
        await deleteGlossaryTerm(id);
        setGlossaryEntries(prev => prev?.filter(entry => entry?.id !== id));
        setSelectedEntries(prev => prev?.filter(entryId => entryId !== id));
      } catch (err) {
        console.error('Error deleting glossary term:', err);
        alert('Failed to delete glossary term: ' + err?.message);
      }
    }
  };

  const handleAudioUpload = (id, file) => {
    // Validate file type
    const validTypes = ['audio/mp3', 'audio/mpeg', 'audio/wav'];
    if (!validTypes?.includes(file?.type)) {
      alert('Please upload a valid audio file (MP3 or WAV)');
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file?.size > maxSize) {
      alert('Audio file size must be less than 10MB');
      return;
    }

    const audioUrl = URL.createObjectURL(file);
    setGlossaryEntries(prev =>
      prev?.map(entry =>
        entry?.id === id
          ? { ...entry, audioUrl, lastUpdated: new Date() }
          : entry
      )
    );
  };

  const handleAddWord = async (newWord) => {
    try {
      await addGlossaryTerm(newWord, userProfile?.id);
      // Reload data to include new term
      await loadGlossaryData();
    } catch (err) {
      console.error('Error adding glossary term:', err);
      alert('Failed to add glossary term: ' + err?.message);
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(`Delete ${selectedEntries?.length} selected words?`)) {
      try {
        await Promise.all(
          selectedEntries?.map(id => deleteGlossaryTerm(id))
        );
        setGlossaryEntries(prev =>
          prev?.filter(entry => !selectedEntries?.includes(entry?.id))
        );
        setSelectedEntries([]);
      } catch (err) {
        console.error('Error deleting glossary terms:', err);
        alert('Failed to delete some glossary terms: ' + err?.message);
      }
    }
  };

  const handleBulkExport = () => {
    const entriesToExport = selectedEntries?.length > 0
      ? glossaryEntries?.filter(entry => selectedEntries?.includes(entry?.id))
      : glossaryEntries;

    const csvContent = [
      ['Word', 'Pronunciation', 'Definition', 'Plays', 'Frequency', 'Has Audio']?.join(','),
      ...entriesToExport?.map(entry =>
        [
          entry?.word,
          entry?.pronunciation,
          `"${entry?.definition}"`,
          `"${entry?.plays?.join('; ')}"`,
          entry?.frequency,
          entry?.audioUrl ? 'Yes' : 'No'
        ]?.join(',')
      )
    ]?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `glossary_export_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    link?.click();
    URL.revokeObjectURL(url);
  };

  const handleViewUsage = (entry) => {
    setUsageModalEntry(entry);
  };

  const handleAITermsGenerated = (terms) => {
    setGeneratedTerms(terms);
    setIsReviewModalOpen(true);
  };

  const handleSaveGeneratedTerms = async (termsToSave) => {
    try {
      const savePromises = termsToSave?.map(term => 
        addGlossaryTerm(term, userProfile?.id)
      );
      await Promise.all(savePromises);
      await loadGlossaryData();
      setIsReviewModalOpen(false);
      setGeneratedTerms([]);
    } catch (err) {
      console.error('Error saving generated terms:', err);
      alert('Failed to save some glossary terms: ' + err?.message);
    }
  };

  const handleRetryAudio = async (termId, termWord) => {
    try {
      setAudioRetryStatus(`Generating audio for "${termWord}"...`);
      await generateAudioForTerm(termId, termWord);
      setAudioRetryStatus(`Audio generated for "${termWord}"`);
      await loadGlossaryData();
      setTimeout(() => setAudioRetryStatus(null), 3000);
    } catch (err) {
      console.error('Error retrying audio generation:', err);
      setAudioRetryStatus(`Failed to generate audio for "${termWord}"`);
      setTimeout(() => setAudioRetryStatus(null), 4000);
    }
  };

  const handleRetryAllFailedAudio = async () => {
    try {
      setAudioRetrying(true);
      setAudioRetryStatus('Retrying all failed/pending audio generation...');
      const result = await retryFailedAudioGeneration(null, (completed, total, currentTerm) => {
        if (currentTerm) {
          setAudioRetryStatus(`Generating audio: "${currentTerm}" (${completed + 1}/${total})...`);
        }
      });
      setAudioRetryStatus(`Audio retry complete: ${result?.succeeded} succeeded, ${result?.failed} failed`);
      await loadGlossaryData();
      setTimeout(() => setAudioRetryStatus(null), 4000);
    } catch (err) {
      console.error('Error retrying all audio:', err);
      setAudioRetryStatus('Audio retry failed. Please try again.');
      setTimeout(() => setAudioRetryStatus(null), 4000);
    } finally {
      setAudioRetrying(false);
    }
  };

  const filteredEntries = filterEntries();
  const totalWords = glossaryEntries?.length || 0;
  const completeWords = glossaryEntries?.filter(e => e?.definition && e?.audioUrl)?.length || 0;
  const incompleteWords = glossaryEntries?.filter(e => !e?.definition)?.length || 0;
  const pendingAudioCount = glossaryEntries?.filter(
    e => e?.audioGenerationStatus === 'pending' || e?.audioGenerationStatus === 'failed'
  )?.length || 0;

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation
        user={userProfile}
        isAuthenticated={isAuthenticated}
        notifications={mockNotifications}
      />
      <main className="main-content">
        <BreadcrumbTrail />

        <div className="max-w-screen-2xl mx-auto px-6 py-8">
          <GlossaryHeader
            totalWords={totalWords}
            completeWords={completeWords}
            completedWords={completeWords}
            incompleteWords={incompleteWords}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onAddWord={() => setIsAddModalOpen(true)}
            onAddNew={() => setIsAddModalOpen(true)}
          />

          {/* AI Glossary Generator */}
          <div className="mb-6">
            <AIGlossaryGenerator
              plays={plays}
              onTermsGenerated={handleAITermsGenerated}
            />
          </div>

          {/* Audio generation status banner */}
          {(audioRetryStatus || pendingAudioCount > 0) && (
            <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Icon name="Volume2" size={18} className="text-amber-600 dark:text-amber-400 flex-shrink-0" />
                <div>
                  {audioRetryStatus ? (
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">{audioRetryStatus}</p>
                  ) : (
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                      {pendingAudioCount} term{pendingAudioCount !== 1 ? 's' : ''} with pending or failed pronunciation audio
                    </p>
                  )}
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                    AI-generated audio is flagged for admin review before publishing.
                  </p>
                </div>
              </div>
              {pendingAudioCount > 0 && !audioRetrying && (
                <Button
                  variant="outline"
                  size="sm"
                  iconName="RefreshCw"
                  iconPosition="left"
                  onClick={handleRetryAllFailedAudio}
                  disabled={audioRetrying}
                >
                  Generate All
                </Button>
              )}
              {audioRetrying && (
                <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                  <Icon name="Loader" size={16} className="animate-spin" />
                  <span className="text-xs">Processing...</span>
                </div>
              )}
            </div>
          )}

          <FilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            playFilter={playFilter}
            onPlayFilterChange={setPlayFilter}
            alphabetFilter={alphabetFilter}
            onAlphabetFilterChange={setAlphabetFilter}
            plays={plays}
          />
          {selectedEntries?.length > 0 && (
            <BulkOperationsPanel
              selectedCount={selectedEntries?.length}
              onBulkDelete={handleBulkDelete}
              onBulkExport={handleBulkExport}
              onBulkImport={() => {}}
              onClearSelection={() => setSelectedEntries([])}
            />
          )}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading glossary entries...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-error">Error loading glossary: {error}</p>
            </div>
          ) : filteredEntries?.length === 0 ? (
            <EmptyState
              searchTerm={searchTerm}
              hasFilters={statusFilter !== 'all' || playFilter !== 'all' || alphabetFilter !== 'all'}
              onClearFilters={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setPlayFilter('all');
                setAlphabetFilter('all');
              }}
              onAddWord={() => setIsAddModalOpen(true)}
              onAddNew={() => setIsAddModalOpen(true)}
            />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredEntries?.map(entry => (
                <div key={entry?.id} className="flex items-start gap-3">
                  <Checkbox
                    checked={selectedEntries?.includes(entry?.id)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedEntries(prev => [...prev, entry?.id]);
                      } else {
                        setSelectedEntries(prev => prev?.filter(id => id !== entry?.id));
                      }
                    }}
                    className="mt-6"
                  />
                  <GlossaryEntryCard
                    entry={entry}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onAudioUpload={handleAudioUpload}
                    onViewUsage={handleViewUsage}
                    onPlayAudio={() => {}}
                    onRetryAudio={handleRetryAudio}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Review Generated Terms Modal */}
      {isReviewModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-card-foreground flex items-center gap-2">
                    <Icon name="Sparkles" size={24} className="text-purple-500" />
                    Review Generated Terms
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    {generatedTerms?.length} term{generatedTerms?.length !== 1 ? 's' : ''} generated. Review and select which ones to add to your glossary.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setIsReviewModalOpen(false);
                    setGeneratedTerms([]);
                  }}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name="X" size={24} />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {generatedTerms?.map((term, index) => (
                  <div
                    key={index}
                    className="bg-muted rounded-lg p-4 border border-border hover:border-purple-300 dark:hover:border-purple-700 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold text-card-foreground">
                            {term?.word}
                          </h3>
                          {term?.pronunciation && (
                            <span className="text-sm text-muted-foreground font-mono bg-background px-2 py-0.5 rounded">
                              {term?.pronunciation}
                            </span>
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            term?.difficulty === 'easy' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                              : term?.difficulty === 'hard' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                          }`}>
                            {term?.difficulty}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {term?.definition}
                        </p>
                      </div>
                      <Checkbox
                        checked={term?.selected !== false}
                        onCheckedChange={(checked) => {
                          setGeneratedTerms(prev =>
                            prev?.map((t, i) =>
                              i === index ? { ...t, selected: checked } : t
                            )
                          );
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-border bg-muted/30">
              <div className="flex items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground">
                  {generatedTerms?.filter(t => t?.selected !== false)?.length} of {generatedTerms?.length} terms selected
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsReviewModalOpen(false);
                      setGeneratedTerms([]);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      const termsToSave = generatedTerms?.filter(t => t?.selected !== false);
                      if (termsToSave?.length > 0) {
                        handleSaveGeneratedTerms(termsToSave);
                      }
                    }}
                    disabled={generatedTerms?.filter(t => t?.selected !== false)?.length === 0}
                    className="bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white"
                  >
                    <Icon name="Check" size={16} />
                    Add Selected Terms
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isAddModalOpen && (
        <AddWordModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onAdd={handleAddWord}
          plays={plays}
        />
      )}
      {usageModalEntry && (
        <UsageDetailsModal
          entry={usageModalEntry}
          onClose={() => setUsageModalEntry(null)}
        />
      )}
    </div>
  );
};

export default GlossaryManagement;